import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import LoadingScreen from '../components/shared/LoadingScreen';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import AccountHomeRedirect from './AccountHomeRedirect';

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> }
      ]
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <AccountHomeRedirect />
        </AuthGuard>
      )
    },
    {
      path: '/agentic',
      element: (
        <AuthGuard>
          <AgenticHomePage />
        </AuthGuard>
      )
    },
    { path: '/agentic/welcome', element: <AgenticWelcomePage /> },
    {
      path: '/agentic/settings/products',
      element: (
        <AuthGuard>
          <AgenticProductsPage />
        </AuthGuard>
      )
    },
    {
      path: '/agentic/settings/products/import',
      element: (
        <AuthGuard>
          <AgenticImportProductsPage />
        </AuthGuard>
      )
    },
    {
      path: '/agentic/settings/billing',
      element: (
        <AuthGuard>
          <AgenticBillingPage />
        </AuthGuard>
      )
    },
    {
      path: '/agentic/settings/integrations',
      element: (
        <AuthGuard>
          <AgenticIntegrationsPage />
        </AuthGuard>
      )
    },
    {
      path: '/agentic/settings/general',
      element: (
        <AuthGuard>
          <AgenticGeneralSettingsPage />
        </AuthGuard>
      )
    },
    {
      path: '/agentic/custom-flows',
      element: (
        <AuthGuard>
          <AgenticCustomFlowsPage />
        </AuthGuard>
      )
    },
    {
      path: '/agentic/style',
      element: (
        <AuthGuard>
          <AgenticStylePage />
        </AuthGuard>
      )
    },
    {
      path: '/agentic/tickets',
      element: (
        <AuthGuard>
          <AgenticTicketsPage />
        </AuthGuard>
      )
    },
    {
      path: '/agentic/knowledge',
      element: (
        <AuthGuard>
          <AgenticKnowledgePage />
        </AuthGuard>
      )
    },
    {
      path: '/agentic/knowledge/manual',
      element: (
        <AuthGuard>
          <AgenticManualKnowledgePage />
        </AuthGuard>
      )
    },
    {
      path: '/agentic/knowledge/word',
      element: (
        <AuthGuard>
          <AgenticWordKnowledgePage />
        </AuthGuard>
      )
    },
    {
      path: '/agentic/knowledge/notion',
      element: (
        <AuthGuard>
          <AgenticNotionKnowledgePage />
        </AuthGuard>
      )
    },
    ...['website'].map((source) => ({
      path: `/agentic/knowledge/${source}`,
      element: (
        <AuthGuard>
          <AgenticKnowledgeSourcePage source={source} />
        </AuthGuard>
      )
    })),
    // Dashboard Routes
    {
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: 'admin', element: <AdminDashboard /> },
        { path: 'admin/import-qa/:userId', element: <ImportQaProduct /> },
        { path: 'admin/processing-verify/:userId', element: <ManualTicketVerify /> },
        { path: 'admin/delete-old-logs', element: <DeleteOldLogs /> },
        { path: 'admin/processing-logs', element: <ProcessingLogs /> },
        { path: 'admin/processing-entry/:logId/:userId', element: <AdminProcessingEntry /> },
        { path: 'admin/new-feature-usage', element: <NewFeatureUsagePage /> },
        { path: 'admin/global-stats', element: <GlobalStats /> },
        { path: 'admin/manage-products/:userId', element: <ManageProducts /> },
        { path: 'admin/error-logs', element: <ErrorLogsPage /> },
        { path: 'admin/flow-errors', element: <FlowErrorsPage /> },
        { path: 'admin/manage-billing/:userId', element: <ManageBilling /> },
        { path: 'admin/ticket-analysis/:userId/:trackerId', element: <TicketAnalysisPage /> },
        { path: 'admin/gap-analysis-trackers/:userId', element: <GapAnalysisTrackersPage /> },
        { path: 'admin/gap-report/:userId/:trackerId', element: <GapReportPage /> },
        { path: 'admin/mining/:userId', element: <MiningDashboard /> },
        { path: 'admin/mining/past-tickets/:userId', element: <MinePastTicketsPage /> },
        { path: 'admin/mining/past-tickets/run/:miningRunId', element: <OnePastTicketsRunPage /> },
        { path: 'admin/mining/gap/:userId', element: <MineGapTicketsPage /> },
        { path: 'admin/mining/gap/run/:miningRunId', element: <OneGapMiningRunPage /> },
        { path: 'admin/memory/:userId', element: <ShopMemoryAdminPage /> },
        { path: 'admin/account/tickets/:userId', element: <AccountTicketsPage /> },
        { path: 'admin/account/tickets/:userId/ticket/:logId', element: <AdminAccountTicketDetailsPage /> },
        { path: 'admin/account/webhooks/:userId', element: <ManageWebhooksPage /> },
        { path: 'admin/account/:userId/flows/run/:flowRunId', element: <AdminAccountFlowRunPage /> },
        { path: 'admin/account-overview/:userId', element: <AdminAccountOverviewPage /> },
        { path: 'admin/escalations', element: <EscalationsDashboardPage /> },
        { path: 'admin/processing-report', element: <ProcessingReportPage /> },
        { path: 'admin/initial-decisions', element: <InitialDecisionsDashboardPage /> },
        { path: 'admin/result-evals', element: <ResultEvalsDashboardPage /> },
        { path: 'admin/memory-creation-logs', element: <MemoryCreationLogsDashboardPage /> },
        { path: 'admin/answer-augmentations', element: <AnswerAugmentationsDashboardPage /> },
        { path: 'home', element: <HomePage /> },
        { path: 'billing/success', element: <PaymentMethodSuccess /> },
        { path: 'trial', element: <TrialZone /> },
        { path: 'qa', element: <QA /> },
        { path: 'add-ticket-policy', element: <AddTicketPolicy /> },
        { path: 'tov', element: <BotCharacter /> },
        { path: 'bot-list', element: <ToneOfVoice /> },
        { path: 'settings/general', element: <Settings /> },
        { path: 'settings/connectors', element: <SettingsConnectors /> },
        { path: 'settings/actions', element: <SettingsActions /> },
        { path: 'settings/automation', element: <SettingsAutomation /> },
        { path: 'settings/products', element: <ProductSettings /> },
        { path: 'settings/products/import', element: <ImportProducts /> },
        { path: 'settings/billing', element: <SettingsBilling /> },
        { path: 'settings/plan-select', element: <PlanSelect /> },
        { path: 'settings/suggestions', element: <SettingsSuggestions /> },
        { path: 'settings/notion-import', element: <NotionImport /> },
        { path: 'settings/zendesk-macros-import', element: <ZendeskMacrosImport /> },
        { path: 'settings/custom-flows', element: <CustomFlowsPage /> },
        { path: 'settings/create-flow', element: <CreateFlowPage /> },
        { path: 'settings/flow/:flowId', element: <EditFlowPage /> },
        { path: 'settings/flow/:flowId/runs', element: <FlowRunsPage /> },
        { path: 'settings/flow-tests/:flowId', element: <FlowTestsListPage /> },
        { path: 'settings/flow-test/:flowId/:testId', element: <SingleFlowTestPage /> },
        { path: 'flows/create-from-template', element: <CreateFlowFromTemplatePage /> },
        { path: 'flows/run/:flowRunId', element: <SingleFlowRunPage /> },
        {
          path: 'testing_o9283jd',
          element: <TestingDash />
        },
        {
          path: 'testing_o9283jd/sunshine',
          element: <SunshineTesting />
        },
        { path: 'create-test', element: <CreateTestCase /> },
        { path: 'processing', element: <ProcessingDash /> },
        { path: 'usage', element: <UsageTracking /> },
        { path: 'processing-entry/:logId', element: <ProcessingEntry /> },
        { path: 'processing/replacement/check-images/:logId', element: <CheckReplaceImages /> },
        { path: 'tickets', element: <CustomerTicketsList /> },
        { path: 'ticket/:logId', element: <CustomerTicketDetails /> },
        { path: 'stats', element: <StatsDashboard /> },
        { path: 'stats/configure', element: <StatsConfigurationPage /> },
        { path: 'playground', element: <PlaygroundHomePage /> },
        { path: 'playground/create-test', element: <CreateTestCasePage /> },
        { path: 'playground/case/:testCaseId', element: <TestCasePage /> },
        { path: 'playground/test-web-widget', element: <TestWebWidget /> },
        { path: 'followups', element: <FollowUpsHomePage /> },
        { path: 'followups/edit/:campaignId', element: <EditFollowupCampaign /> },
        { path: 'followups/campaign/:campaignId', element: <CampaignOverview /> }
      ]
    },
    {
      path: '/service_oauth/callback/gorgias',
      element: (
        <AuthGuard>
          <GorgiasOAuthCallback />
        </AuthGuard>
      )
    },
    {
      path: '/service_oauth/callback/:service',
      element: (
        <OAuthCallback />
      )
    },
    { path: '/optin/:ticket_log_id', element: <OptInDecision /> },
    { path: '/install/gorgias', element: <GorgiasInstallPage /> },
    { path: '/shopify', element: <ConnectShopifyMarketplace /> },
    { path: '/register-callback-shopify', element: <AuthorizeGuestVennie /> },
    { path: '/report-wrong/:link_type/:processing_entry_id/:answer_action_id', element: <ReportWrongPage /> },
    { path: '/terms-and-privacy', element: <TermsAndPrivacy /> },
    { path: '/welcome', element: <Onboarding /> },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// Admin
const AdminDashboard = Loadable(lazy(() => import('../pages/admin/AdminDashboard')));
const ImportQaProduct = Loadable(lazy(() => import('../pages/admin/ImportQaProduct')));
const ManualTicketVerify = Loadable(lazy(() => import('../pages/admin/ManualTicketVerify')));
const DeleteOldLogs = Loadable(lazy(() => import('../pages/admin/DeleteOldLogs')));
const ProcessingLogs = Loadable(lazy(() => import('../pages/admin/ProcessingLogs')));
const AdminProcessingEntry = Loadable(lazy(() => import('../pages/admin/AdminProcessingEntry')));
const NewFeatureUsagePage = Loadable(lazy(() => import('../pages/admin/NewFeatureUsagePage')));
const GlobalStats = Loadable(lazy(() => import('../pages/admin/GlobalStats')));
const ManageProducts = Loadable(lazy(() => import('../pages/admin/ManageProducts')));
const ErrorLogsPage = Loadable(lazy(() => import('../pages/admin/ErrorLogsPage')));
const ManageBilling = Loadable(lazy(() => import('../pages/admin/ManageBilling')));
const TicketAnalysisPage = Loadable(lazy(() => import('../pages/admin/gap/TicketAnalysisPage')));
const GapAnalysisTrackersPage = Loadable(lazy(() => import('../pages/admin/gap/GapAnalysisTrackersPage')));
const GapReportPage = Loadable(lazy(() => import('../pages/admin/gap/GapReportPage')));
const FlowErrorsPage = Loadable(lazy(() => import('../pages/admin/CustomFlowErrorsPage')));
const AccountTicketsPage = Loadable(lazy(() => import('../pages/admin/AccountTicketsPage')));
const AdminAccountTicketDetailsPage = Loadable(lazy(() => import('../pages/admin/AdminAccountTicketDetailsPage')));
const ManageWebhooksPage = Loadable(lazy(() => import('../pages/admin/ManageWebhooksPage')));
const AdminAccountFlowRunPage = Loadable(lazy(() => import('../pages/admin/AdminAccountFlowRunPage')));
const AdminAccountOverviewPage = Loadable(lazy(() => import('../pages/admin/AdminAccountOverviewPage')));
const EscalationsDashboardPage = Loadable(lazy(() => import('../pages/admin/EscalationsDashboardPage')));
const ProcessingReportPage = Loadable(lazy(() => import('../pages/admin/ProcessingReportPage')));
const InitialDecisionsDashboardPage = Loadable(lazy(() => import('../pages/admin/InitialDecisionsDashboardPage')));
const ResultEvalsDashboardPage = Loadable(lazy(() => import('../pages/admin/ResultEvalsDashboardPage')));
const MemoryCreationLogsDashboardPage = Loadable(lazy(() => import('../pages/admin/MemoryCreationLogsDashboardPage')));
const AnswerAugmentationsDashboardPage = Loadable(lazy(() => import('../pages/admin/AnswerAugmentationsDashboardPage')));

// Admin - Mining
const MiningDashboard = Loadable(lazy(() => import('../pages/admin/mining/MiningDashboard')));
const MinePastTicketsPage = Loadable(lazy(() => import('../pages/admin/mining/MinePastTicketsPage')));
const OnePastTicketsRunPage = Loadable(lazy(() => import('../pages/admin/mining/OnePastTicketsRunPage')));
const MineGapTicketsPage = Loadable(lazy(() => import('../pages/admin/mining/MineGapTicketsPage')));
const OneGapMiningRunPage = Loadable(lazy(() => import('../pages/admin/mining/OneGapMiningRunPage')));

// Admin - Memory
const ShopMemoryAdminPage = Loadable(lazy(() => import('../pages/admin/memory/ShopMemoryAdmin')));

// Dashboard
const AgenticHomePage = Loadable(lazy(() => import('../pages/agentic/HomePage')));
const AgenticProductsPage = Loadable(lazy(() => import('../pages/agentic/settings/products/ProductsPage')));
const AgenticImportProductsPage = Loadable(lazy(() => import('../pages/agentic/settings/products/ImportProductsPage')));
const AgenticBillingPage = Loadable(lazy(() => import('../pages/agentic/settings/billing/BillingPage')));
const AgenticIntegrationsPage = Loadable(lazy(() => import('../pages/agentic/settings/integrations/IntegrationsPage')));
const AgenticGeneralSettingsPage = Loadable(lazy(() => import('../pages/agentic/settings/general/GeneralSettingsPage')));
const AgenticCustomFlowsPage = Loadable(lazy(() => import('../pages/agentic/custom-flows/CustomFlowsPage')));
const AgenticStylePage = Loadable(lazy(() => import('../pages/agentic/style/StylePage')));
const AgenticTicketsPage = Loadable(lazy(() => import('../pages/agentic/tickets/TicketsPage')));
const AgenticWelcomePage = Loadable(lazy(() => import('../pages/agentic/onboarding/WelcomePage')));
const AgenticKnowledgePage = Loadable(lazy(() => import('../pages/agentic/knowledge/KnowledgePage')));
const AgenticKnowledgeSourcePage = Loadable(lazy(() => import('../pages/agentic/knowledge/KnowledgeSourcePlaceholderPage')));
const AgenticManualKnowledgePage = Loadable(lazy(() => import('../pages/agentic/knowledge/ManualKnowledgePage')));
const AgenticWordKnowledgePage = Loadable(lazy(() => import('../pages/agentic/knowledge/WordKnowledgePage')));
const AgenticNotionKnowledgePage = Loadable(lazy(() => import('../pages/agentic/knowledge/NotionKnowledgePage')));
const HomePage = Loadable(lazy(() => import('../pages/dashboard/home/HomePage')));
const ToneOfVoice = Loadable(lazy(() => import('../pages/dashboard/ToneOfVoice')));
const BotCharacter = Loadable(lazy(() => import('../pages/dashboard/BotCharacter')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
const OAuthCallback = Loadable(lazy(() => import('../pages/OauthCallback')));
const GorgiasOAuthCallback = Loadable(lazy(() => import('../pages/OauthCallback/GorgiasOAuthCallback')));
const GorgiasInstallPage = Loadable(lazy(() => import('../pages/install/GorgiasInstallPage')));
const Settings = Loadable(lazy(() => import('../pages/dashboard/Settings')));
const SettingsConnectors = Loadable(lazy(() => import('../pages/dashboard/Settings/connectors')));
const SettingsActions = Loadable(lazy(() => import('../pages/dashboard/actions/ActionsPage')));
const SettingsBilling = Loadable(lazy(() => import('../pages/dashboard/Settings/billing/SettingsBilling')));
const SettingsAutomation = Loadable(lazy(() => import('../pages/dashboard/Settings/AutomationSettings')));
const ProductSettings = Loadable(lazy(() => import('../pages/dashboard/Settings/ProductSettings')));
const ImportProducts = Loadable(lazy(() => import('../pages/dashboard/Settings/products/ImportProducts')));
const TrialZone = Loadable(lazy(() => import('../pages/dashboard/TrialZone')));
const QA = Loadable(lazy(() => import('../pages/dashboard/Faq')));
const AddTicketPolicy = Loadable(lazy(() => import('../pages/dashboard/AddTicketPolicy')));
const PlanSelect = Loadable(lazy(() => import('../pages/dashboard/PlanSelect')));
const OptInDecision = Loadable(lazy(() => import('../pages/dashboard/optIn/OptInDecision')));
const TermsAndPrivacy = Loadable(lazy(() => import('../pages/TermsAndPrivacy')));
const Onboarding = Loadable(lazy(() => import('../pages/dashboard/onboarding/WelcomePage')));
const PaymentMethodSuccess = Loadable(lazy(() => import('../pages/dashboard/billing/PaymentMethodSuccess')));
const StatsDashboard = Loadable(lazy(() => import('../pages/dashboard/stats/StatsDashboard')));
const StatsConfigurationPage = Loadable(lazy(() => import('../pages/dashboard/stats/StatsConfiguration')));
const SettingsSuggestions = Loadable(lazy(() => import('../pages/dashboard/Settings/settings_suggestions/SettingsSuggestionsPage')));
const ZendeskMacrosImport = Loadable(lazy(() => import('../pages/ZendeskMacrosImport')));
const CustomerTicketsList = Loadable(lazy(() => import('../pages/tickets')));
const CustomerTicketDetails = Loadable(lazy(() => import('../pages/ticket')));
const NotionImport = Loadable(lazy(() => import('../pages/NotionImport')));

// Flows
const CustomFlowsPage = Loadable(lazy(() => import('../pages/dashboard/Settings/flows/CustomFlows')));
const CreateFlowPage = Loadable(lazy(() => import('../pages/dashboard/Settings/flows/CreateFlow')));
const EditFlowPage = Loadable(lazy(() => import('../pages/dashboard/Settings/flows/EditFlow')));
const FlowRunsPage = Loadable(lazy(() => import('../pages/dashboard/Settings/flows/FlowRunsPage')));
const FlowTestsListPage = Loadable(lazy(() => import('../pages/dashboard/Settings/flows/FlowTestsListPage')));
const SingleFlowTestPage = Loadable(lazy(() => import('../pages/dashboard/Settings/flows/SingleFlowTest')));
const CreateFlowFromTemplatePage = Loadable(lazy(() => import('../pages/dashboard/Settings/flows/CreateFlowFromTemplate')));
const SingleFlowRunPage = Loadable(lazy(() => import('../pages/flow/SingleFlowRunPage')));

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const Register = Loadable(lazy(() => import('../pages/authentication/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/authentication/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')));

// Processing
const TestingDash = Loadable(lazy(() => import('../pages/testing/dash')));
const CreateTestCase = Loadable(lazy(() => import('../pages/testing/create')));
const ProcessingDash = Loadable(lazy(() => import('../pages/processing/dash')));
const ProcessingEntry = Loadable(lazy(() => import('../pages/processing/entry')));
const CheckReplaceImages = Loadable(lazy(() => import('../pages/processing/replacement/check-images')));
const UsageTracking = Loadable(lazy(() => import('../pages/processing/usage/UsageTrackingPage')));
const ReportWrongPage = Loadable(lazy(() => import('../pages/processing/suggestion/ReportWrongPage')));
const PlaygroundHomePage = Loadable(lazy(() => import('../pages/dashboard/playground/PlaygroundHomePage')));
const CreateTestCasePage = Loadable(lazy(() => import('../pages/dashboard/playground/CreateTestCasePage')));
const TestCasePage = Loadable(lazy(() => import('../pages/dashboard/playground/TestCasePage')));
const TestWebWidget = Loadable(lazy(() => import('../pages/dashboard/playground/TestWebWidget')));

// Shopify
const ConnectShopifyMarketplace = Loadable(lazy(() => import('../pages/ConnectShopifyMarketplace')));
const AuthorizeGuestVennie = Loadable(lazy(() => import('../components/shopify/AuthorizeGuestVennie')));

// Testing
const SunshineTesting = Loadable(lazy(() => import('../pages/testing/integrations/SunshineTesting')));

// Sales
const FollowUpsHomePage = Loadable(lazy(() => import('../pages/sales/FollowUpsHomePage')));
const EditFollowupCampaign = Loadable(lazy(() => import('../pages/sales/EditFollowupCampaign')));
const CampaignOverview = Loadable(lazy(() => import('../pages/sales/CampaignOverview')));
